import {
  CHARTER_NUMBER,
  COMPANY_ADDRESS_FULL,
  COMPANY_FACTS,
  COMPANY_LOCATION,
  COMPANY_NAME,
  COMPANY_SHORT,
  COMPANY_TAGLINE,
  CONTACT_EMAIL,
  EFFECTIVE_DATE,
  GET_INVOLVED,
  HOW_IT_WORKS,
  MAP_URL,
  MISSION,
  ORG_TYPE,
  PROGRAMS,
  TAX_NOTICE,
  WHO_WE_SERVE,
  WHY_EQUINE,
} from './constants';

// === Inline SVG icons (currentColor-stroked, sized via className) ===

function IconSun({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4" />
    </svg>
  );
}

function IconSparkle({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M12 3 13.5 9 19.5 10.5 13.5 12 12 18 10.5 12 4.5 10.5 10.5 9 12 3Z" />
      <path d="M19 17v3M17.5 18.5h3M5 4v2M4 5h2" />
    </svg>
  );
}

function IconShield({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M12 3 4 6v6c0 4.5 3.5 7.5 8 9 4.5-1.5 8-4.5 8-9V6l-8-3Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function IconHeart({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M12 21s-7-4.5-9.5-9c-1.5-2.7-.4-6 2.5-7 1.7-.6 4 .2 5 2 1-1.8 3.3-2.6 5-2 2.9 1 4 4.3 2.5 7C19 16.5 12 21 12 21Z" />
    </svg>
  );
}

const AUDIENCE_ICONS = {
  youth: IconSun,
  autistic: IconSparkle,
  veterans: IconShield,
  'mental-health': IconHeart,
};

// === Building blocks ===

function CcMonogram() {
  return (
    <span
      className="flex h-10 w-10 items-center justify-center rounded-lg bg-cc-saddle text-cc-cream font-black text-sm tracking-wider"
      aria-hidden="true"
    >
      CC
    </span>
  );
}

function Header() {
  const navItems = [
    { label: 'Mission', href: '#about' },
    { label: 'Programs', href: '#programs' },
    { label: 'Visit', href: '#location' },
    { label: 'Get Involved', href: '#involved' },
  ];
  return (
    <header className="sticky top-0 z-30 border-b border-cc-saddle/10 bg-cc-cream/85 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4">
        <a
          href="#top"
          className="flex items-center gap-3 font-semibold text-cc-ink"
          aria-label={`${COMPANY_SHORT} home`}
        >
          <CcMonogram />
          <span className="tracking-wide">{COMPANY_SHORT}</span>
        </a>
        <nav
          aria-label="Primary"
          className="hidden items-center gap-7 text-sm font-medium text-cc-mute md:flex"
        >
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="transition hover:text-cc-saddle">
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href="#involved"
          className="rounded-lg bg-cc-blue px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-cc-blue-d focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cc-blue focus-visible:ring-offset-2 focus-visible:ring-offset-cc-cream"
        >
          Get Involved
        </a>
      </div>
    </header>
  );
}

function AudienceCard({ audience }) {
  const Icon = AUDIENCE_ICONS[audience.id];
  return (
    <article className="rounded-xl bg-white p-6 shadow-lift">
      <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-cc-blue/10 text-cc-blue">
        {Icon ? <Icon className="h-6 w-6" /> : null}
      </div>
      <h3 className="text-lg font-bold text-cc-ink">{audience.title}</h3>
      <p className="mt-3 text-sm leading-6 text-cc-mute">{audience.summary}</p>
    </article>
  );
}

function AudienceStrip() {
  return (
    <>
      <p className="mb-4 text-center text-xs font-semibold uppercase tracking-[0.22em] text-cc-saddle/80">
        Who we serve
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {WHO_WE_SERVE.map((audience) => (
          <AudienceCard key={audience.id} audience={audience} />
        ))}
      </div>
    </>
  );
}

function Hero() {
  // Mobile: cards render inline below the hero copy (no absolute positioning),
  //   because 4 stacked cards would overflow upward into the headline.
  // Tablet+: cards float absolute at hero bottom and overlap the next section,
  //   creating the "Ranch Immersive" depth effect.
  return (
    <section className="relative bg-hero-ranch overflow-visible">
      <div className="mx-auto max-w-6xl px-5 pt-16 pb-12 sm:pt-20 sm:pb-80 md:pt-28 md:pb-72 lg:pb-72">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cc-saddle sm:text-sm">
            Equine Therapy · Barnesville, OH
          </p>
          <h1 className="mt-6 font-black tracking-tight text-cc-ink text-5xl sm:text-6xl lg:text-7xl">
            {COMPANY_TAGLINE}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-cc-mute sm:text-lg sm:leading-8">
            {MISSION}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="#programs"
              className="w-full rounded-lg bg-cc-blue px-6 py-3 font-semibold text-white shadow-sm transition hover:bg-cc-blue-d sm:w-auto"
            >
              Discover Programs
            </a>
            <a
              href="#location"
              className="w-full rounded-lg border-2 border-cc-saddle px-6 py-3 font-semibold text-cc-saddle transition hover:bg-cc-saddle hover:text-cc-cream sm:w-auto"
            >
              Visit the Ranch
            </a>
          </div>
        </div>

        {/* Mobile inline strip — no overlap, clean stack. */}
        <div className="mt-14 sm:hidden">
          <AudienceStrip />
        </div>
      </div>

      {/* Tablet+ floating strip — overlaps into the Mission section. */}
      <div className="absolute inset-x-0 bottom-0 z-20 hidden translate-y-1/3 sm:block lg:translate-y-1/2">
        <div className="mx-auto max-w-6xl px-5">
          <AudienceStrip />
        </div>
      </div>
    </section>
  );
}

function FactCard({ label, value }) {
  return (
    <div className="rounded-lg border border-cc-saddle/15 bg-white p-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-cc-mute">{label}</p>
      <p className="mt-2 text-xl font-bold text-cc-ink">{value}</p>
    </div>
  );
}

function Mission() {
  // Mobile: cards are inline above (in the hero), no overlap to clear → small pt.
  // Tablet+: floating cards overlap from the hero by translate-y-1/3 (sm) / 1/2 (lg)
  //   so we add generous top padding to push the headline below them.
  return (
    <section id="about" className="bg-white pt-12 sm:pt-52 md:pt-64 lg:pt-72">
      <div className="mx-auto max-w-6xl px-5 pb-20">
        <div className="grid gap-12 md:grid-cols-[1.2fr_0.8fr] md:items-start">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cc-saddle">
              Our Mission
            </p>
            <p className="mt-4 font-black tracking-tight text-cc-ink text-2xl leading-snug sm:text-3xl lg:text-4xl">
              &ldquo;{MISSION}&rdquo;
            </p>
            <p className="mt-6 text-sm text-cc-mute">
              From our Articles of Incorporation, October 4, 2023.
            </p>
          </div>
          <div className="grid gap-3">
            {COMPANY_FACTS.map((fact) => (
              <FactCard key={fact.label} label={fact.label} value={fact.value} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyEquine() {
  return (
    <section id="why" className="bg-cc-sage/15">
      <div className="mx-auto max-w-6xl px-5 py-20">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cc-saddle">
            Why equine therapy
          </p>
          <h2 className="mt-3 text-3xl font-bold text-cc-ink sm:text-4xl">
            Horses meet people where words fall short.
          </h2>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {WHY_EQUINE.map((item) => (
            <article key={item.title} className="rounded-lg bg-white p-6 shadow-soft">
              <div className="h-1 w-12 rounded-full bg-cc-blue" />
              <h3 className="mt-5 text-xl font-bold text-cc-ink">{item.title}</h3>
              <p className="mt-3 leading-7 text-cc-mute">{item.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Programs() {
  return (
    <section id="programs" className="bg-white">
      <div className="mx-auto max-w-6xl px-5 py-20">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cc-saddle">
            Programs
          </p>
          <h2 className="mt-3 text-3xl font-bold text-cc-ink sm:text-4xl">
            Sessions on the ranch.
          </h2>
          <p className="mt-4 leading-7 text-cc-mute">
            Every session is ground-based and tailored. Reach out to plan a fit.
          </p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {PROGRAMS.map((program) => (
            <article
              key={program.id}
              className="rounded-lg border border-cc-saddle/15 bg-white p-6 transition hover:border-cc-saddle/40"
            >
              <h3 className="text-xl font-bold text-cc-ink">{program.title}</h3>
              <p className="mt-3 leading-7 text-cc-mute">{program.summary}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="process" className="bg-cc-saddle text-cc-cream">
      <div className="mx-auto max-w-6xl px-5 py-20">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.18em]">
            How it works
          </p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Three simple steps to begin.</h2>
        </div>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {HOW_IT_WORKS.map((step) => (
            <div key={step.step} className="border-t border-cc-cream/20 pt-6">
              <p className="font-black text-cc-blue text-5xl">{step.step}</p>
              <h3 className="mt-3 text-xl font-bold">{step.title}</h3>
              <p className="mt-3 leading-7 text-cc-cream/80">{step.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GetInvolved() {
  return (
    <section id="involved" className="bg-cc-cream">
      <div className="mx-auto max-w-6xl px-5 py-20">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cc-saddle">
            Get involved
          </p>
          <h2 className="mt-3 text-3xl font-bold text-cc-ink sm:text-4xl">
            Three ways to support our work.
          </h2>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {GET_INVOLVED.map((entry) => {
            const subject = encodeURIComponent(`${entry.cta} — Cody's Corral`);
            return (
              <article
                key={entry.audience}
                className="flex flex-col rounded-lg bg-white p-6 shadow-soft"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-cc-blue">
                  {entry.audience}
                </p>
                <h3 className="mt-2 text-xl font-bold text-cc-ink">{entry.cta}</h3>
                <p className="mt-3 flex-1 leading-7 text-cc-mute">{entry.body}</p>
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=${subject}`}
                  className="mt-6 inline-flex justify-center rounded-lg bg-cc-blue px-5 py-3 font-semibold text-white transition hover:bg-cc-blue-d focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cc-blue focus-visible:ring-offset-2 focus-visible:ring-offset-cc-cream"
                >
                  {entry.cta}
                </a>
              </article>
            );
          })}
        </div>
        <p className="mt-10 max-w-3xl text-sm text-cc-mute">{TAX_NOTICE}</p>
      </div>
    </section>
  );
}

function Location() {
  return (
    <section id="location" className="bg-white">
      <div className="mx-auto max-w-6xl px-5 py-20">
        <div className="grid gap-10 md:grid-cols-2 md:items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cc-saddle">
              Visit
            </p>
            <h2 className="mt-3 text-3xl font-bold text-cc-ink sm:text-4xl">{COMPANY_LOCATION}</h2>
            <p className="mt-4 leading-7 text-cc-mute">
              We&rsquo;re a working ranch in Belmont County, eastern Ohio. Please reach out before
              visiting so we can plan around the horses&rsquo; day.
            </p>
          </div>
          <div className="rounded-lg border border-cc-saddle/15 bg-cc-cream p-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-cc-mute">Address</p>
            <p className="mt-3 text-xl font-bold text-cc-ink">{COMPANY_ADDRESS_FULL}</p>
            <a
              href={MAP_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex rounded-lg bg-cc-saddle px-5 py-3 font-semibold text-cc-cream transition hover:bg-cc-saddle-d"
            >
              Open map
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Credentials() {
  return (
    <section id="credentials" className="bg-cc-cream">
      <div className="mx-auto max-w-6xl px-5 py-20">
        <div className="rounded-lg border border-cc-saddle/15 bg-white p-8 md:p-10">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cc-saddle">
            Credentials
          </p>
          <h2 className="mt-3 text-2xl font-bold text-cc-ink sm:text-3xl">
            Public Ohio Secretary of State record.
          </h2>
          <dl className="mt-8 grid gap-6 sm:grid-cols-3">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-cc-mute">
                Charter Number
              </dt>
              <dd className="mt-2 text-lg font-bold text-cc-ink">{CHARTER_NUMBER}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-cc-mute">
                Effective Date
              </dt>
              <dd className="mt-2 text-lg font-bold text-cc-ink">{EFFECTIVE_DATE}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-cc-mute">
                Org Type
              </dt>
              <dd className="mt-2 text-lg font-bold text-cc-ink">{ORG_TYPE}</dd>
            </div>
          </dl>
          <p className="mt-8 text-sm text-cc-mute">{TAX_NOTICE}</p>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-cc-saddle-d text-cc-cream/80">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-5 py-10 text-sm md:flex-row md:items-center md:justify-between">
        <div>
          <p className="font-bold text-cc-cream">{COMPANY_NAME}</p>
          <p className="mt-1 text-cc-cream/60">{COMPANY_LOCATION}</p>
        </div>
        <div className="md:text-right">
          <p className="text-cc-cream/70">{TAX_NOTICE}</p>
          <p className="mt-2 text-cc-cream/60">
            &copy; {new Date().getFullYear()} {COMPANY_SHORT} ·{' '}
            <a href="#credentials" className="underline-offset-4 hover:underline">
              Legal
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen text-cc-ink">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-cc-saddle focus:px-3 focus:py-2 focus:text-cc-cream"
      >
        Skip to content
      </a>
      <Header />
      <main id="main">
        <Hero />
        <Mission />
        <WhyEquine />
        <Programs />
        <HowItWorks />
        <GetInvolved />
        <Location />
        <Credentials />
      </main>
      <Footer />
    </div>
  );
}

export default App;
